/**
 * Managed firehose subscription (F6, firehose §3.6/§7): at-least-once in,
 * exactly-once out — dedupe by event id (bounded FIFO ring), cursor = max seq
 * seen, resubscribe from cursor on iterator failure with Clock-driven backoff
 * (ADR-13: no raw timers).
 *
 * Verification is two-phase (envelope.ts verifyParsed): structural parse →
 * ingest identity events into the directory → verify. That ordering lets a
 * genesis event arriving on the stream verify ITSELF as well as its
 * successors. Identity events are verified by the fold's own verdict, not by
 * chain-head comparison — a rotateKey is legitimately signed by the PREVIOUS
 * head, which post-ingest head comparison would wrongly flag stale (the fold
 * is the one audited authority on chain validity, directory.ts).
 */
import type { Clock, TimerHandle } from "@beacon/clock";
import type { CborValue } from "@beacon/crypto";
import type { FirehoseTransport, SeqEvent, SubscribeFilter } from "@beacon/transport";
import { parseWire, verifyParsed } from "./envelope.js";
import { IDENTITY_TYPES, type IdentityDirectory } from "./directory.js";

export interface ReceivedEvent {
  seq: number;
  json: Record<string, unknown>;
  cbor: Record<string, CborValue>;
  /** true = signature checked (chain-head for content, fold-accepted for identity events). */
  verified: boolean;
}

export interface SubscriptionContext {
  directory: IdentityDirectory;
  clock: Clock;
  /** "ifResolvable" (default): verify when the signer resolves, else deliver verified=false.
   *  "require": drop events whose signer cannot be resolved. (Failed signatures are
   *  always dropped under either policy.) */
  verify?: "ifResolvable" | "require";
  /** Called for every dropped event (evt + parse/verify reason) and for
   *  reconnects (evt=null, reason "reconnect: <cause>"). */
  onSkip?: (evt: SeqEvent | null, reason: string) => void;
  /** FH-5: raised when the relay signals an ephemeral purge-floor reset (hello
   *  reset:true) — the resume cursor fell below the floor and the backfill was
   *  skipped. Seedable consumers re-seed from their read model; non-seedable
   *  consumers accept the clamp (the relay already streams live from the floor). */
  onReset?: () => void;
  /** Raised once per connect when the transport signals end-of-replay (the relay's `caughtUp`
   *  frame): everything up to `seq` matching the filter has been delivered, live from here.
   *  A bounded-backfill consumer uses it as its "loaded" signal. */
  onCaughtUp?: (seq: number) => void;
  reconnectDelayMicros?: number; // default 1_000_000 (1 s sim-time)
  dedupeCapacity?: number; // default 65536 ids, FIFO eviction
}

const DEFAULT_RECONNECT_DELAY_MICROS = 1_000_000;
const DEFAULT_DEDUPE_CAPACITY = 65536;

/**
 * SINGLE-CONSUMER: run exactly one for-await loop per Subscription. The cursor,
 * dedupe ring and the close() detach handles below are all per-instance, not
 * per-iterator — a second concurrent consumer would race the cursor and split
 * the dedupe stream (the underlying transport queue is single-consumer too).
 * Want fan-out? Create one Subscription per consumer.
 */
export class Subscription implements AsyncIterable<ReceivedEvent> {
  #cursor: number;
  #closed = false;
  #seen = new Set<string>();
  #seenQueue: string[] = [];
  /** Live transport iterator the generator is currently draining (close() detaches it). */
  #inner: AsyncIterator<SeqEvent> | null = null;
  /** Pending backoff sleep (close() cancels the timer and wakes the generator). */
  #backoff: { handle: TimerHandle; wake: () => void } | null = null;

  constructor(
    private readonly transport: FirehoseTransport,
    private readonly filter: Pick<SubscribeFilter, "cells" | "types" | "class"> & { sinceSeq?: number; lookback?: number },
    private readonly ctx: SubscriptionContext,
  ) {
    this.#cursor = filter.sinceSeq ?? -1; // -1 sentinel = live-tail only
  }

  /** Max stream seq seen (resubscribe point); -1 until the first event in live-tail mode. */
  get cursor(): number {
    return this.#cursor;
  }

  /** Ends the consumer's for-await promptly even when it is parked: detaches the
   *  live transport iterator (inner.return) and cancels+wakes a pending backoff. */
  async close(): Promise<void> {
    this.#closed = true;
    const backoff = this.#backoff;
    if (backoff) {
      this.#backoff = null;
      backoff.handle.cancel();
      backoff.wake();
    }
    const inner = this.#inner;
    this.#inner = null;
    // return() resolves a parked next() with done=true (AsyncQueue contract);
    // best-effort on foreign transports.
    if (inner) await detach(inner);
  }

  // NOTE: the transport subscribe is issued synchronously on the iterator's
  // first next() (no await before it) — callers may publish immediately after
  // starting a live-tail consumer without losing events. Tests rely on this.
  //
  // Error scoping: ONLY transport failures (subscribe()/next()) enter the
  // reconnect/backoff path. A throw out of #handle (or a consumer's onSkip
  // callback) is a programming error — it propagates to the for-await loop
  // instead of being misreported as a transport reconnect and retried.
  async *[Symbol.asyncIterator](): AsyncIterator<ReceivedEvent> {
    const delay = this.ctx.reconnectDelayMicros ?? DEFAULT_RECONNECT_DELAY_MICROS;
    while (!this.#closed) {
      let inner: AsyncIterator<SeqEvent>;
      try {
        const filter: SubscribeFilter = { cells: this.filter.cells, types: this.filter.types };
        if (this.filter.class) filter.class = this.filter.class;
        if (this.#cursor >= 0) filter.sinceSeq = this.#cursor;
        // First connect (no cursor yet): a `lookback` does a bounded backfill instead of
        // sinceSeq:0. Reconnects resume from the advanced cursor, so lookback applies once.
        else if (this.filter.lookback !== undefined) filter.lookback = this.filter.lookback;
        inner = this.transport.subscribe(filter, { onReset: this.ctx.onReset, onCaughtUp: this.ctx.onCaughtUp })[Symbol.asyncIterator]();
      } catch (err) {
        if (this.#closed) return;
        await this.#backoffAfterReconnect(err, delay);
        continue;
      }
      this.#inner = inner;
      try {
        while (true) {
          let r: IteratorResult<SeqEvent>;
          try {
            r = await inner.next();
          } catch (err) {
            if (this.#closed) return;
            await this.#backoffAfterReconnect(err, delay);
            break; // resubscribe from the cursor
          }
          if (r.done) return; // transport ended cleanly (transport/close detach)
          if (this.#closed) return;
          const evt = r.value;
          this.#cursor = Math.max(this.#cursor, evt.seq);
          const out = await this.#handle(evt); // throws propagate (see scoping note)
          if (out) yield out;
        }
      } finally {
        // Detach on every exit (consumer break/throw, #handle throw, reconnect):
        // never leave a transport queue accumulating for an abandoned iterator.
        if (this.#inner === inner) this.#inner = null;
        await detach(inner);
      }
    }
  }

  /** Notify FIRST, then schedule: sim-driven tests advance the clock from
   *  onSkip (the backoff timer is pending one microtask after this call). */
  async #backoffAfterReconnect(err: unknown, delayMicros: number): Promise<void> {
    this.ctx.onSkip?.(null, `reconnect: ${String(err)}`);
    await new Promise<void>((resolve) => {
      const handle = this.ctx.clock.after(delayMicros, () => {
        this.#backoff = null;
        resolve();
      });
      this.#backoff = { handle, wake: resolve };
    });
  }

  async #handle(evt: SeqEvent): Promise<ReceivedEvent | null> {
    if (this.#seen.has(evt.id)) return null;
    this.#remember(evt.id);
    return verifyEvent(evt, this.ctx);
  }

  #remember(id: string): void {
    const cap = this.ctx.dedupeCapacity ?? DEFAULT_DEDUPE_CAPACITY;
    this.#seen.add(id);
    this.#seenQueue.push(id);
    while (this.#seenQueue.length > cap) this.#seen.delete(this.#seenQueue.shift() as string);
  }
}

/** Context for verifying a single event: the directory (identity ingest + key
 *  resolution), the verify policy, and an optional skip callback. Shared by the
 *  live Subscription and one-shot backfill (BeaconClient.backfill). */
export type VerifyContext = Pick<SubscriptionContext, "directory" | "verify" | "onSkip">;

/**
 * Structurally parse and verify one event, returning a ReceivedEvent or null
 * (skip). Identical to the per-event work the live Subscription does AFTER its
 * dedupe gate — extracted so backfill reuses the exact same parse/verify path
 * (identity ingest, fold verdict for identity events, signature check for
 * content). Dedupe is the caller's concern (the live ring; the message store
 * for backfill). Phase ordering matches the file header: parse → ingest identity
 * → verify.
 */
export async function verifyEvent(evt: SeqEvent, ctx: VerifyContext): Promise<ReceivedEvent | null> {
  // Phase 1 — structural: decode → lexicon → wire-header consistency.
  const parsed = await parseWire(evt);
  if (!parsed.ok) {
    ctx.onSkip?.(evt, parsed.reason);
    return null;
  }
  const did = parsed.cbor.identityId as string;
  // Phase 2 — verify (after ingesting identity events, see header comment).
  let verified: boolean;
  if (typeof parsed.cbor.$type === "string" && IDENTITY_TYPES.has(parsed.cbor.$type)) {
    ctx.directory.ingest(parsed.cbor);
    const verdict = await ctx.directory.verdict(did, evt.id);
    if (verdict !== "accepted" && verdict !== "unresolved") {
      ctx.onSkip?.(evt, verdict); // fold-rejected: stale-key / bad-sig / prev-mismatch / …
      return null;
    }
    verified = verdict === "accepted";
  } else {
    const v = await verifyParsed(parsed.cbor, async (d) => {
      return (await ctx.directory.resolve(d))?.signingKey ?? null;
    });
    if (!v.ok) {
      ctx.onSkip?.(evt, v.reason);
      return null;
    }
    verified = v.verified;
  }
  if (!verified && (ctx.verify ?? "ifResolvable") === "require") {
    ctx.onSkip?.(evt, "unverified");
    return null;
  }
  return { seq: evt.seq, json: parsed.json, cbor: parsed.cbor, verified };
}

function noop(): void {}

/** Best-effort iterator detach: swallow sync throws and async rejections alike. */
async function detach(it: AsyncIterator<SeqEvent>): Promise<void> {
  try {
    await Promise.resolve(it.return?.()).then(noop, noop);
  } catch {
    // a foreign iterator's return() threw synchronously — detach stays best-effort
  }
}
